<template>
  <div
    v-if="report"
    :class="{ 'border-grey-600 border': !report.hide_border }"
    class="group mx-auto flex h-[150px] w-full flex-row justify-between rounded-lg bg-white shadow-pm"
    data-testid="report-card"
    :data-file-type="getReportFileType(report)"
  >
    <div class="relative h-full w-[125px] flex-shrink-0 rounded-l-lg bg-s-300">
      <img
        :src="$cloudinary.resize(reportImage, 'report-thumbnail')"
        class="flex size-full h-full max-h-full rounded-l-lg object-cover"
        :class="{ 'grayscale group-hover:grayscale-0': useGrayScaleBackground }"
        alt="Report forecasts"
        data-testid="report-card-image"
      />
      <OverlayCard
        v-if="report.show_overlay"
        :date-report="date"
        :type="fileType || 'pdf'"
      />
      <CardLabel
        :type="fileType || 'pdf'"
        :show-label="false"
        class="absolute left-3 top-3 z-10"
      />
    </div>
    <div class="flex w-full flex-col justify-between p-3">
      <div>
        <div class="flex flex-col items-start justify-between gap-1">
          <span v-show="showType" class="text-xs font-semibold text-s-700">
            {{ type }}
          </span>
          <h3
            class="line-clamp-2 text-sm font-semibold xl:text-lg"
            data-testid="report-card-title"
          >
            {{ reportTitle }}
          </h3>
        </div>
        <div
          v-if="reportDescriptions"
          class="pt-4"
          data-testid="report-card-description"
        >
          <span v-for="(description, index) in reportDescriptions" :key="index">
            {{ description }}
          </span>
        </div>
      </div>
      <ReportCardFooter
        :report-id="reportId"
        :report-name="reportName"
        :report-file="reportFile"
        :report-has-access="reportHasAccess"
        :initial-fields="initialFields"
        :show-copy-link="showCopyLink"
        :category="category"
        :link="reportLink"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns'

import OverlayCard from '@/components/portals/OverlayCard.vue'
import CardLabel from '~/components/portals/CardLabel.vue'
import ReportCardFooter from '~/components/portals/ReportCardFooter.vue'
import { getReportFileType } from '~/src/utils/reports/image'

const extensionMap = {
  pdf: 'pdf',
  xls: 'xlsx',
  xlsm: 'xlsx',
  xlsx: 'xlsx',
  csv: 'xlsx',
  pptx: 'ppt',
  ppt: 'ppt',
}

interface Props {
  report?: any
  category?: string
  initialFields?: string[]
  showCopyLink?: boolean
  useGrayScaleBackground?: boolean
  srcImage?: string
}

const props = withDefaults(defineProps<Props>(), {
  report: () => null,
  category: null,
  initialFields: () => [],
  showCopyLink: false,
  useGrayScaleBackground: false,
  srcImage: '',
})

const route = useRoute()
const page = route.fullPath
const { $cloudinary } = useNuxtApp()

const reportTitle = computed(() => props.report?.title)
const reportName = computed(() => props.report?.file_name)
const reportId = computed(() => props.report?.id)
const reportDescriptions = computed(() => props.report?.descriptions || false)
const reportLink = computed(() => {
  const baseUrl = import.meta.client
    ? window.location.href.split('/').slice(0, 3).join('/')
    : ''
  const link = props.report.link
    ? `/${props.report.link}`
    : `${page}/report/${reportId.value}`
  return `${baseUrl}${link}`
})
const date = computed(() =>
  props.report?.date ? format(new Date(props.report.date), 'dd MMM yyyy') : '',
)
const reportFile = computed(() => ({
  ...props.report,
  category: props.category,
}))
const reportHasAccess = computed(() => props.report?.has_access)
const type = computed(() => props.report?.assessment_type)
const reportImage = computed(
  () => props.srcImage || props.report?.images?.desktop,
)
const fileType = computed(() => {
  const extension = props.report.file_extension
  return extensionMap[extension] || ''
})
const showType = computed(
  () =>
    type.value &&
    props.report.market_type &&
    props.report.market_type !== 'forecast' &&
    props.report.market_type !== 'esg',
)
</script>
